import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PERMISSION, can, channelShortName, inboxApi, useAuth } from '@wa/shared';
import type { ConversationCounts, ScopeCounts } from '@wa/shared';
import {
  DATE_RANGES, SCOPE_LABEL, STATUSES_FOR, STATUS_LABEL, defaultFilterFor, homeScope, isCleared, rangeToDays, scopesFor,
  type DateRange, type InboxFilter, type InboxScope, type InboxStatus,
} from '@/inbox/filters';

/**
 * The filter column, matched to the screenshots.
 *
 * Four accordion groups, each holding the same statuses, then the narrowing
 * controls beneath them. The structure is the model: a scope crossed with a
 * status, both of which the API treats as independent predicates, so "the unread
 * ones in my chats" needs no name of its own and no second endpoint.
 *
 * <b>It collapses to a strip of the four group icons.</b> Its own toggle, separate
 * from the navigation rail's — they are different questions ("which section am I
 * in" and "how am I narrowing this list") and tying them together would mean
 * widening the rail to see a filter. Collapsed, the icons still switch groups, so
 * it is a narrower control rather than a disabled one.
 *
 * Counts arrive for all four groups in one request. Twenty badges must not become
 * twenty scans: the counts endpoint gained one grouped query rather than the panel
 * gaining twenty calls.
 */
interface Props {
  counts: ConversationCounts | undefined;
  filter: InboxFilter;
  onChange: (next: Partial<InboxFilter>) => void;
  /** Present only where the panel is a sheet — below `xl`. */
  onClose?: () => void;
  /**
   * Collapsed state lives with the grid, not here.
   *
   * The column this sits in is a fixed track, so a panel that shrank itself
   * would leave two hundred pixels of empty column beside it — which is exactly
   * what the first version did. Whoever owns the width owns the state.
   */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

export function InboxFilterPanel({
  counts, filter, onChange, onClose, collapsed, onCollapsedChange,
}: Props) {
  const { user } = useAuth();
  const seesAll = can(user, PERMISSION.inboxViewAll);
  const scopes = scopesFor(seesAll);

  // Loaded here because this is the only place that wants the whole list, and all
  // three change rarely enough to cache hard.
  const tags = useQuery({ queryKey: ['tags'], queryFn: inboxApi.tags, staleTime: 300_000 });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels, staleTime: 300_000 });
  const agents = useQuery({ queryKey: ['agents'], queryFn: inboxApi.agents, staleTime: 300_000 });

  // A sheet is already a narrow thing somebody opened on purpose; collapsing it
  // inside itself would leave a 44px strip floating in a full-width overlay.
  const canCollapse = onClose === undefined;

  if (collapsed && canCollapse) {
    return (
      <nav
        aria-label="Conversation filters"
        className="flex h-full min-h-0 w-full flex-col items-center gap-1 bg-(--color-surface) py-2"
      >
        <PanelToggle collapsed onClick={() => onCollapsedChange(false)} />

        <div className="mt-1 flex flex-col gap-1">
          {scopes.map((scope) => (
            <button
              key={scope}
              type="button"
              onClick={() => onChange({ scope, status: STATUSES_FOR[scope][0] })}
              title={SCOPE_LABEL[scope]}
              aria-label={SCOPE_LABEL[scope]}
              aria-current={filter.scope === scope ? 'true' : undefined}
              className={[
                'tap grid size-8 place-items-center rounded-(--radius-field) transition-colors',
                filter.scope === scope
                  ? 'bg-(--color-brand-wash) text-(--color-brand)'
                  : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)',
              ].join(' ')}
            >
              <ScopeIcon scope={scope} />
            </button>
          ))}
        </div>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Conversation filters"
      className="pane-scroll flex h-full min-h-0 flex-col overflow-y-auto bg-(--color-surface)"
    >
      <header className="flex shrink-0 items-center gap-1 border-b border-(--color-line-dim) px-3 py-2.5">
        <h2 className="min-w-0 flex-1 truncate text-lead font-semibold tracking-tight">Inbox</h2>

        {!isCleared(filter, seesAll) && (
          <button
            type="button"
            onClick={() => onChange(defaultFilterFor(seesAll))}
            className="tap rounded-(--radius-chip) px-2 py-0.5 text-micro text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
          >
            Clear
          </button>
        )}

        <Help />
        {canCollapse && <PanelToggle collapsed={false} onClick={() => onCollapsedChange(true)} />}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="tap grid size-7 shrink-0 place-items-center rounded-(--radius-field) text-(--color-muted) hover:bg-(--color-sunken)"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </header>

      <div className="shrink-0 px-2">
        {scopes.map((scope) => (
          <Group
            key={scope}
            scope={scope}
            counts={counts?.scopes?.[scope]}
            home={scope === homeScope(seesAll)}
            filter={filter}
            onChange={onChange}
          />
        ))}
      </div>

      <div className="mt-2 flex flex-col gap-3 px-3 pb-4">
        <Section label="View">
          {/* Groups is built and says what it is waiting for rather than being
              hidden. Meta's Groups API needs an Official Business Account, which
              PSM does not hold — see the build reference. The list explains it;
              this control is not a lie because it leads somewhere that does. */}
          <div className="flex gap-1 rounded-(--radius-field) border border-(--color-line) p-0.5">
            {([['chats', 'All Chats'], ['groups', 'Groups']] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => onChange({ surface: value })}
                aria-pressed={filter.surface === value}
                className={[
                  'tap flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-(--radius-field) px-1 text-body transition-colors',
                  filter.surface === value
                    ? 'bg-(--color-brand-wash) font-medium text-(--color-brand)'
                    : 'text-(--color-muted) hover:bg-(--color-sunken)',
                ].join(' ')}
              >
                {/* 18px here, not the 20 the group rows use: at 20 the label
                    "All Chats" ran into an ellipsis inside its own half of the
                    control, and a segmented button that cannot show its own word
                    is a button somebody has to guess at. */}
                <span className="shrink-0 [&>svg]:size-[18px]">
                  {value === 'chats' ? <ScopeIcon scope="all" /> : <GroupsIcon />}
                </span>
                <span className="min-w-0 truncate">{label}</span>
              </button>
            ))}
          </div>
        </Section>

        <Section label="Team member">
          <Select
            value={filter.assignedTo === null ? '' : String(filter.assignedTo)}
            onChange={(v) => onChange({ assignedTo: v ? Number(v) : null })}
            placeholder="Filter by member"
            options={(agents.data ?? []).map((a) => ({ value: String(a.id), label: a.displayName }))}
          />
        </Section>

        <Section label="Tags">
          <Select
            value={filter.tagId === null ? '' : String(filter.tagId)}
            onChange={(v) => onChange({ tagId: v ? Number(v) : null })}
            placeholder="Filter by tags"
            options={(tags.data ?? []).map((t) => ({ value: String(t.id), label: t.name }))}
          />
        </Section>

        <Section label="Channels">
          <Select
            value={filter.channelId === null ? '' : String(filter.channelId)}
            onChange={(v) => onChange({ channelId: v ? Number(v) : null })}
            placeholder="Filter by Channels"
            options={(channels.data ?? []).map((c) => ({
              value: String(c.id),
              // The name if it has one, the number if not. A channel list of three
              // identical "+91 89281 931…" entries is not a filter.
              label: channelShortName(c),
            }))}
          />
        </Section>

        <Section label="Date range">
          <Select
            value={filter.range}
            onChange={(v) => {
              const range = v as DateRange;
              // Custom keeps whatever the two inputs already hold, so choosing it
              // does not wipe a range somebody has just typed.
              onChange(range === 'custom'
                ? { range }
                : { range, ...rangeToDays(range) });
            }}
            options={DATE_RANGES.map((r) => ({ value: r.value, label: r.label }))}
          />

          {filter.range === 'custom' && (
            <div className="mt-1.5 flex gap-1">
              <input
                type="date"
                aria-label="From"
                value={filter.from}
                max={filter.to || undefined}
                onChange={(e) => onChange({ from: e.target.value })}
                className="min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-1.5 py-1 text-micro"
              />
              <input
                type="date"
                aria-label="To"
                value={filter.to}
                min={filter.from || undefined}
                onChange={(e) => onChange({ to: e.target.value })}
                className="min-w-0 flex-1 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-1.5 py-1 text-micro"
              />
            </div>
          )}
        </Section>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ group -- */

/**
 * One accordion group.
 *
 * The group somebody is inside is always open — a group that shut itself when you
 * picked something in it would hide what you had just chosen. The rest remember
 * whether they were opened by hand.
 */
function Group({ scope, counts, home, filter, onChange }: {
  scope: InboxScope;
  counts: ScopeCounts | undefined;
  /** The group a shift starts in; it is the one that starts open. */
  home: boolean;
  filter: InboxFilter;
  onChange: (next: Partial<InboxFilter>) => void;
}) {
  const current = filter.scope === scope;

  /*
   * Open is the reader's business and nobody else's.
   *
   * The first version forced the current group open — `current || opened` — which
   * meant the group you were working in was the one group you could not collapse.
   * Clicking its header set the local flag false and `current` kept it open
   * anyway, so the chevron moved and nothing else did. The panel being matched
   * lets all four be shut at once while a filter from one of them is still
   * applied; the list's own heading is what says which.
   *
   * The home group starts open because that is where a shift starts. Nothing
   * else opens or closes on its own.
   */
  const [open, setOpen] = useState(home);

  return (
    <div className="border-b border-(--color-line-dim)">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          // Opening a group selects it, and selects its first status: landing on
          // a group with no status chosen would silently apply the previous
          // group's status to this one.
          if (!open && !current) onChange({ scope, status: STATUSES_FOR[scope][0] });
        }}
        aria-expanded={open}
        className="tap flex h-11 w-full items-center gap-2.5 rounded-(--radius-field) px-2 text-left text-body transition-colors hover:bg-(--color-sunken)"
      >
        <span className="shrink-0 text-(--color-muted)">
          <ScopeIcon scope={scope} />
        </span>
        <span className="min-w-0 flex-1 truncate text-(--color-ink-soft)">
          {SCOPE_LABEL[scope]}
        </span>
        <Chevron open={open} />
      </button>

      {/* 0fr to 1fr on a grid row, which is the one way to transition to a height
          nobody has measured. `overflow-hidden` on the inner element is what makes
          the clip follow the row rather than the content spilling out of it. */}
      <div
        className={[
          'grid transition-[grid-template-rows] duration-200 ease-out',
          'motion-reduce:transition-none',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        ].join(' ')}
      >
        <div className="overflow-hidden">
          <div className="pb-1">
            {STATUSES_FOR[scope].map((status) => (
              <StatusRow
                key={status}
                status={status}
                // No badge on All. It is the group's own total and the list's
                // heading already carries it — the panel being matched leaves it
                // off for the same reason, and a badge that repeats the number
                // beside it is a number to reconcile rather than to read.
                count={status === 'all' ? undefined : counts?.[status]}
                active={current && filter.status === status}
                onClick={() => onChange({ scope, status })}
                // A collapsed group's rows are still in the DOM, holding the
                // height the transition animates to. They must not be tab stops
                // or a keyboard user tabs into a panel they cannot see.
                hidden={!open}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ status, count, active, hidden, onClick }: {
  status: InboxStatus;
  count: number | undefined;
  active: boolean;
  hidden: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={hidden ? -1 : undefined}
      aria-hidden={hidden || undefined}
      aria-current={active ? 'true' : undefined}
      className={[
        'tap flex h-9 w-full items-center gap-2 rounded-(--radius-field) pr-2 pl-10 text-left text-body transition-colors',
        active
          ? 'bg-(--color-brand-wash) font-semibold text-(--color-brand)'
          : 'text-(--color-ink-soft) hover:bg-(--color-sunken)',
      ].join(' ')}
    >
      <span className="min-w-0 flex-1 truncate">{STATUS_LABEL[status]}</span>

      {/* Zero is not shown. A badge reading 0 has to be read before it can be
          ignored, on a panel with twenty of them. */}
      {count !== undefined && count > 0 && (
        <span
          className={[
            'shrink-0 rounded-full px-1.5 py-px text-micro font-semibold tabular-nums',
            active
              ? 'bg-(--color-brand) text-(--color-brand-ink)'
              : 'bg-(--color-sunken) text-(--color-muted)',
          ].join(' ')}
        >
          {/* 1K+, not 1,024. The badge is a size, not a figure — nobody acts on
              the difference between 1,024 and 1,031, and four digits push the
              label into an ellipsis. */}
          {count > 999 ? '1K+' : count}
        </span>
      )}

      {/* Awaiting reply carries a small clock, as the design being matched does.
          It is the one status that is about elapsed time rather than state. */}
      {status === 'awaiting' && !active && (
        <span aria-hidden="true" className="shrink-0 text-(--color-faint)">
          <svg viewBox="0 0 20 20" width="13" height="13">
            <circle cx="10" cy="10" r="6.2" {...stroke} />
            <path d="M10 6.8v3.4l2.2 1.3" {...stroke} />
          </svg>
        </span>
      )}
    </button>
  );
}

/* --------------------------------------------------------------- plumbing -- */

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {/* Sentence case, not uppercase. The panel being matched sets these as plain
          small labels, and a column of shouted words above every control makes a
          narrow panel read as louder than the list beside it. */}
      {/* 14/21 at 500 on the reference — a section label, not a caption. */}
      <p className="mb-1.5 text-body font-medium text-(--color-ink-soft)">{label}</p>
      {children}
    </div>
  );
}

function Select({ value, onChange, placeholder, options }: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2.5 text-body text-(--color-ink-soft) outline-none focus:border-(--color-brand)"
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/** The collapse control. One glyph, rotated, so the two states cannot drift apart. */
function PanelToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Show filters' : 'Hide filters'}
      title={collapsed ? 'Show filters' : 'Hide filters'}
      className="tap grid size-7 shrink-0 place-items-center rounded-(--radius-field) text-(--color-muted) transition-colors hover:bg-(--color-sunken) hover:text-(--color-ink)"
    >
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
        <rect x="2.8" y="3.6" width="14.4" height="12.8" rx="2" {...stroke} />
        <path d="M7.8 3.6v12.8" {...stroke} />
        <path
          d={collapsed ? 'M11 8.2 13.2 10 11 11.8' : 'M13.6 8.2 11.4 10l2.2 1.8'}
          {...stroke}
        />
      </svg>
    </button>
  );
}

/**
 * The help control.
 *
 * A title and nothing else. It is the one place on this panel where the design
 * being matched opens documentation we do not have — an icon that opened an empty
 * page would be worse than one that explains itself in a tooltip.
 */
function Help() {
  return (
    <span
      role="img"
      aria-label="Groups narrow the list; the controls below narrow it further. Both apply at once."
      title="Groups narrow the list; the controls below narrow it further. Both apply at once."
      className="grid size-7 shrink-0 cursor-help place-items-center rounded-(--radius-field) text-(--color-muted)"
    >
      <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
        <circle cx="10" cy="10" r="7" {...stroke} />
        <path d="M8.2 8.1a1.9 1.9 0 1 1 2.3 2.1v1.1" {...stroke} />
        <path d="M10.4 13.9v.1" {...stroke} />
      </svg>
    </span>
  );
}

/**
 * Down when shut, up when open.
 *
 * Not down-and-right, which is what this was: the panel being matched keeps the
 * chevron on the vertical axis, and a chevron that swings to the horizontal reads
 * as "go there" rather than as "unfold".
 */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"
      className={[
        'shrink-0 text-(--color-muted) transition-transform duration-200 ease-out',
        'motion-reduce:transition-none',
        open ? 'rotate-180' : '',
      ].join(' ')}
    >
      <path d="M5.5 8 10 12.5 14.5 8" {...stroke} />
    </svg>
  );
}

const stroke = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 1.6,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

function ScopeIcon({ scope }: { scope: InboxScope }) {
  switch (scope) {
    case 'all':
      return (
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <path d="M3.6 9.4c0-2.9 2.9-5.2 6.4-5.2s6.4 2.3 6.4 5.2-2.9 5.2-6.4 5.2a8 8 0 0 1-1.9-.2l-2.9 1.4.6-2.4a5 5 0 0 1-2.2-4z" {...stroke} />
        </svg>
      );
    case 'mine':
      return (
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <circle cx="10" cy="10" r="7" {...stroke} />
          <circle cx="10" cy="8.2" r="2.1" {...stroke} />
          <path d="M6.1 15.1a4.3 4.3 0 0 1 7.8 0" {...stroke} />
        </svg>
      );
    case 'sla':
      return (
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <circle cx="10" cy="10.6" r="6" {...stroke} />
          <path d="M10 7.4v3.2l2.2 1.4" {...stroke} />
          <path d="M8 3.2h4" {...stroke} />
        </svg>
      );
    case 'unassigned':
      return (
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
          <circle cx="10" cy="8" r="2.4" {...stroke} />
          <path d="M5.4 15.6a4.9 4.9 0 0 1 9.2 0" {...stroke} />
          <path d="M3.6 3.6 16.4 16.4" {...stroke} />
        </svg>
      );
  }
}

function GroupsIcon() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <circle cx="7.4" cy="7.8" r="2.2" {...stroke} />
      <path d="M3.2 15.4a4.4 4.4 0 0 1 8.4 0" {...stroke} />
      <path d="M13 5.9a2.1 2.1 0 0 1 0 3.9M16.8 15.4a4.2 4.2 0 0 0-1-2.6" {...stroke} />
    </svg>
  );
}
